import { useMemo, useState } from 'react'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Modal } from '@/components/Modal'
import { Select } from '@/components/Select'
import type { DocumentRequirementRules } from '@/types/bundle'
import { documentTypeLabel } from '../labels'

export interface AddDocumentTypeModalProps {
  open: boolean
  onClose: () => void
  documentTypeKeys: string[]
  rules: DocumentRequirementRules
  /** group is 'hard_floor', 'must_have_all', or 'any_of:<index>' into an existing
   * must_have_any_of group. */
  onConfirm: (selectedKeys: string[], group: string) => void
}

const HARD_FLOOR_GROUP = 'hard_floor'
const MUST_HAVE_ALL_GROUP = 'must_have_all'

// Confirmed via live Figma inspection (Design PRD 02, node 8:4): checkbox
// list of document_types.json's own keys + an "Add to group" selector. The
// selector only offers groups that already exist (hard_floor + must_have_all
// + each current must_have_any_of group) - neither PRD describes creating a
// brand-new any-of group.
//
// Defaults to "Must have all" (a real, already-valid value) rather than an
// empty "Select a group…" placeholder, since that's the most common target;
// "Hard floor" is offered as its own independent, always-present option -
// confirmed with the user that it should be authorable separately, even
// though request_validator.py currently checks it exactly the same way as
// must_have_all.
export function AddDocumentTypeModal({
  open,
  onClose,
  documentTypeKeys,
  rules,
  onConfirm,
}: AddDocumentTypeModalProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [group, setGroup] = useState(MUST_HAVE_ALL_GROUP)

  const groupOptions = useMemo(() => {
    const options = [
      { value: MUST_HAVE_ALL_GROUP, label: 'Must have all' },
      { value: HARD_FLOOR_GROUP, label: 'Hard floor' },
    ]
    ;(rules.must_have_any_of ?? []).forEach((_, index) => {
      options.push({ value: `any_of:${index}`, label: `Any of - group ${index + 1}` })
    })
    return options
  }, [rules.must_have_any_of])

  function toggle(key: string) {
    setSelected((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]))
  }

  function handleClose() {
    setSelected([])
    setGroup(MUST_HAVE_ALL_GROUP)
    onClose()
  }

  function handleConfirm() {
    if (selected.length === 0) return
    onConfirm(selected, group)
    handleClose()
  }

  return (
    <Modal
      open={open}
      title="Add Document Type"
      onClose={handleClose}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-control border border-border px-4 py-2 text-body font-medium text-text-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={selected.length === 0}
            onClick={handleConfirm}
            className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Add Selected ({selected.length})
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="add-doc-type-group">
            Add to group:
            <InfoTooltip text="Must have all and Hard floor both make a document type always required (functionally identical today). Any of: at least one document type in that group is required." />
          </label>
          <Select id="add-doc-type-group" value={group} onChange={setGroup} options={groupOptions} />
        </div>

        <div className="flex max-h-[240px] flex-col gap-1 overflow-y-auto">
          {documentTypeKeys.length === 0 ? (
            <span className="text-meta text-text-disabled">No document types defined yet</span>
          ) : (
            documentTypeKeys.map((key) => (
              <label
                key={key}
                className="flex items-center gap-2 rounded-control px-2 py-1.5 hover:bg-surface"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(key)}
                  onChange={() => toggle(key)}
                  className="accent-accent"
                />
                <span className="text-body text-text-primary">{documentTypeLabel(key)}</span>
              </label>
            ))
          )}
        </div>
      </div>
    </Modal>
  )
}
